import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";

interface SmilePackage {
    package_id: number;
    package_name: string;
    price: number;
    currency: string;
    credits: number;
    bonus_credits: number;
    isActive: boolean;
    created_at?: string;
    updated_at?: string;
}

interface SmilePackageState {
    allSmilePackages: SmilePackage[];
    activeSmilePackages: SmilePackage[];
    singleSmilePackage: SmilePackage | null;
    allPackagesPagination: {
        totalCount: number;
        currentPage: number;
        totalPages: number;
        limit: number;
    } | null;
    activePackagesPagination: {
        totalCount: number;
        currentPage: number;
        totalPages: number;
        limit: number;
    } | null;
    loading: boolean;
    error: string | null;
}

const initialState: SmilePackageState = {
    allSmilePackages: [],
    activeSmilePackages: [],
    singleSmilePackage: null,
    allPackagesPagination: null,
    activePackagesPagination: null,
    loading: false,
    error: null,
};

// GET ALL
export const fetchAllSmilePackages = createAsyncThunk(
    "smilePackage/fetchAll",
    async (
        params: { page?: number; limit?: number; search?: string; service?: string } | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get(`/smile-design/packages/getall`, {
                params,
                withCredentials: true,
            });

            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const fetchActiveSmilePackages = createAsyncThunk(
    "smilePackage/fetchActive",
    async (
        params: { page?: number; limit?: number; search?: string; service?: string } | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get(`/smile-design/packages/getactive`, {
                params,
                withCredentials: true,
            });

            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// GET SINGLE
export const fetchSingleSmilePackage = createAsyncThunk(
    "smilePackage/fetchSingle",
    async (
        payload: number | { package_id: number; service?: string },
        { rejectWithValue }
    ) => {
        try {
            const package_id =
                typeof payload === "number" ? payload : payload.package_id;
            const service =
                typeof payload === "number" ? undefined : payload.service;
            const res = await api.get(
                `/smile-design/packages/getone/${package_id}`,
                { params: service ? { service } : undefined, withCredentials: true }
            );

            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// CREATE
export const createSmilePackage = createAsyncThunk(
    "smilePackage/create",
    async (
        data: {
            name: string;
            price: number;
            currency: string;
            credits: number;
            bonus?: number;
            service?: string;
        },
        { rejectWithValue }
    ) => {
        try {
            const res = await api.post(
                `/smile-design/packages/create`,
                data,
                { withCredentials: true }
            );

            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// UPDATE
export const updateSmilePackage = createAsyncThunk(
    "smilePackage/update",
    async (
        {
            package_id,
            data,
        }: {
            package_id: number;
            data: Partial<{
                package_name: string;
                price: number;
                currency: string;
                credits: number;
                bonus_credits: number;
                isActive: boolean;
                service?: string;
            }>;
        },
        { rejectWithValue }
    ) => {
        try {
            const res = await api.put(
                `/smile-design/packages/update/${package_id}`,
                data,
                {
                    params: data.service ? { service: data.service } : undefined,
                    withCredentials: true,
                }
            );

            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

const smilePackageSlice = createSlice({
    name: "smilePackage",
    initialState,
    reducers: {
        clearSmilePackageState: (state) => {
            state.error = null;
        },

        clearSingleSmilePackage: (state) => {
            state.singleSmilePackage = null;
        },
    },

    extraReducers: (builder) => {
        builder

            // FETCH ALL
            .addCase(fetchAllSmilePackages.pending, (state) => {
                state.loading = true;
            })

            .addCase(fetchAllSmilePackages.fulfilled, (state, action) => {
                state.loading = false;
                state.allSmilePackages = action.payload.packages ?? [];
                state.allPackagesPagination = {
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    limit: action.payload.limit,
                };
            })

            .addCase(fetchAllSmilePackages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // FETCH ACTIVE
            .addCase(fetchActiveSmilePackages.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchActiveSmilePackages.fulfilled, (state, action) => {
                state.loading = false;
                state.activeSmilePackages = action.payload.packages ?? [];
                state.activePackagesPagination = {
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    limit: action.payload.limit,
                };
            })
            .addCase(fetchActiveSmilePackages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // FETCH SINGLE
            .addCase(fetchSingleSmilePackage.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSingleSmilePackage.fulfilled, (state, action) => {
                state.loading = false;
                state.singleSmilePackage = action.payload;
            })
            .addCase(fetchSingleSmilePackage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // CREATE
            .addCase(createSmilePackage.pending, (state) => {
                state.loading = true;
            })

            .addCase(createSmilePackage.fulfilled, (state, action) => {
                state.loading = false;
                state.allSmilePackages.unshift(action.payload);
            })

            .addCase(createSmilePackage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // UPDATE
            .addCase(updateSmilePackage.pending, (state) => {
                state.loading = true;
            })

            .addCase(updateSmilePackage.fulfilled, (state, action) => {
                state.loading = false;

                const index = state.allSmilePackages.findIndex(
                    (pkg) => pkg.package_id === action.payload.package_id
                );

                if (index !== -1) {
                    state.allSmilePackages[index] = action.payload;
                }

                if (
                    state.singleSmilePackage?.package_id ===
                    action.payload.package_id
                ) {
                    state.singleSmilePackage = action.payload;
                }
            })

            .addCase(updateSmilePackage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    clearSmilePackageState,
    clearSingleSmilePackage,
} = smilePackageSlice.actions;

export default smilePackageSlice.reducer;